import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useId, useState } from 'react'
import { PopoverSheet } from '@/components/ui/PopoverSheet'
import type { Opportunity } from '@/lib/planning-types'
import { cn } from '@/lib/utils'
import { monthRangeLabel, shiftMonth } from './month'

/**
 * De hele klus een maand opschuiven, in plaats van elke cel opnieuw te typen. Werk
 * dat uitgesteld wordt schuift zelden een halve maand op en zelden een enkele
 * persoon: het is de klus die verhuist, met zijn uren en zijn bedragen.
 *
 * Dezelfde vorm als de maandstepper in de kaartkop: twee pijlen om de knop heen
 * die het paneel opent. Een maand vooruit of terug is daarmee een enkele klik -
 * dat is het geval dat vaak voorkomt - en een grotere sprong vraagt het paneel,
 * waar je hem eerst ziet uitrekenen voor je hem doet.
 */
export function OpportunityShift({
  opportunity,
  onShift,
}: {
  opportunity: Opportunity
  onShift: (months: number) => void
}) {
  const [open, setOpen] = useState(false)
  const [months, setMonths] = useState('2')
  const ids = useId()

  // De looptijd dekt uren en bedragen allebei, dus een vaste-prijs-kans die alleen
  // een bedrag heeft is gewoon te verschuiven en de proefzin noemt de maanden waar
  // dat bedrag staat.
  const first = opportunity.span.first
  const last = opportunity.span.last ?? first
  // Staat er nergens iets, dan valt er niets te verschuiven; de knoppen blijven
  // staan zodat hun uitleg bereikbaar is, maar ze doen niets.
  const empty = first === null

  const step = (delta: number) => {
    if (empty) return
    onShift(delta)
  }

  const value = months.trim().replace(',', '.')
  const parsed = /^[+-]?\d+$/.test(value) ? Number(value) : Number.NaN
  const ready = !empty && Number.isFinite(parsed) && parsed !== 0

  const change = (next: boolean) => {
    setOpen(next)
    // Elke keer schoon open, net als het reeks-paneel: het toont wat je nu gaat
    // doen, niet wat je de vorige keer deed.
    if (next) setMonths('2')
  }

  const submit = () => {
    if (!ready) return
    onShift(parsed)
    setOpen(false)
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="label-caps">Verschuiven</span>
      <span className="inline-flex items-center rounded-lg border border-hairline bg-surface p-0.5">
        <button
          type="button"
          aria-label="Hele kans een maand eerder"
          title={empty ? 'Er staat nog niets om te verschuiven' : 'Hele kans een maand eerder'}
          aria-disabled={empty || undefined}
          onClick={() => step(-1)}
          className={cn('btn btn-ghost h-6 w-6 p-0 text-ink-faint', empty && 'cursor-not-allowed opacity-40')}
        >
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.85} />
        </button>

        <PopoverSheet
          open={open}
          onOpenChange={change}
          title={`Verschuiven: ${opportunity.name}`}
          className="w-72"
          trigger={
            <button
              type="button"
              title={empty ? 'Er staat nog niets om te verschuiven' : 'Meer maanden tegelijk'}
              aria-disabled={empty || undefined}
              className={cn('btn btn-ghost h-6 px-1.5 text-xs text-ink-soft', empty && 'cursor-not-allowed opacity-40')}
            >
              meer
            </button>
          }
        >
          <div className="space-y-3 p-4">
            <div className="grid grid-cols-[auto_1fr] items-center gap-x-3">
              <label htmlFor={`${ids}-maanden`} className="label-caps">
                Maanden
              </label>
              <input
                id={`${ids}-maanden`}
                type="text"
                inputMode="numeric"
                value={months}
                onChange={(event) => setMonths(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') return
                  event.preventDefault()
                  submit()
                }}
                className="field num h-8 w-full text-sm"
              />
            </div>

            <p className="text-xs text-ink-faint">
              {ready && first !== null && last !== null
                ? `${monthRangeLabel([first, last])} wordt ${monthRangeLabel([
                    shiftMonth(first, parsed),
                    shiftMonth(last, parsed),
                  ]).toLowerCase()}`
                : 'Een heel getal; negatief haalt de klus naar voren. Uren en bedragen gaan samen mee.'}
            </p>

            <button type="button" className="btn btn-primary h-8 w-full text-xs" disabled={!ready} onClick={submit}>
              Verschuiven
            </button>
          </div>
        </PopoverSheet>

        <button
          type="button"
          aria-label="Hele kans een maand later"
          title={empty ? 'Er staat nog niets om te verschuiven' : 'Hele kans een maand later'}
          aria-disabled={empty || undefined}
          onClick={() => step(1)}
          className={cn('btn btn-ghost h-6 w-6 p-0 text-ink-faint', empty && 'cursor-not-allowed opacity-40')}
        >
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.85} />
        </button>
      </span>
    </span>
  )
}
